import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useBranding } from '../lib/branding.js';
import { useCapabilities } from '../lib/capabilities.js';
import { api } from '../lib/api.js';
import { SosProvider, useSos } from '../lib/sos.js';
import { showsInNav } from '../lib/simpleMode.js';
import type { Permission, ServerCapabilities } from '../lib/types.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  need?: Permission;
  cap?: keyof ServerCapabilities;
  /** Operator-organisation only (`CurrentUser.canImpersonate`), not just the permission. */
  operator?: boolean;
  /** Other route prefixes that belong to this item (e.g. a display's schedule under Display). */
  also?: string[];
  /** Left out of the sidebar; the page itself stays reachable by URL. */
  hidden?: boolean;
  /** Only in Akarya Box simple mode. */
  simpleOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Home', icon: 'home', simpleOnly: true },
      { to: '/dashboard', label: 'Dashboard', icon: 'dashboard', need: 'reports.view' },
      { to: '/alerts', label: 'Alerts', icon: 'bell', need: 'alerts.view' },
      { to: '/sos', label: 'Emergency SOS', icon: 'siren', need: 'sos.view' },
      { to: '/reports', label: 'Reports', icon: 'chart', need: 'reports.view' },
    ],
  },
  {
    label: 'Screens',
    items: [
      { to: '/displays', label: 'Display', icon: 'screen', need: 'displays.view', also: ['/displays/', '/claim'] },
      { to: '/display-groups', label: 'Display groups', icon: 'groups', need: 'displays.view' },
      { to: '/licenses', label: 'Licences', icon: 'key', need: 'displays.view', cap: 'portableLicenseSales' },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/media', label: 'Media library', icon: 'image', need: 'media.view' },
      { to: '/layouts', label: 'Layouts', icon: 'layout', need: 'layouts.view' },
      { to: '/menu-boards', label: 'Menu boards', icon: 'menu', need: 'menuboards.view', hidden: true },
    ],
  },
];

/** Settings live in one collapsible section, sub-grouped so the long list scans quickly. */
const SETTINGS_NAV: NavGroup[] = [
  {
    label: 'Organisation',
    items: [
      { to: '/settings/users', label: 'Users', icon: 'user', need: 'users.manage' },
      { to: '/settings/roles', label: 'Roles', icon: 'shield', need: 'roles.manage' },
      { to: '/settings/folders', label: 'Folders', icon: 'folder', need: 'folders.manage' },
      { to: '/settings/branding', label: 'Branding', icon: 'palette', need: 'settings.manage' },
      { to: '/settings/billing', label: 'Billing', icon: 'card', need: 'billing.view', cap: 'billing' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { to: '/settings/api-tokens', label: 'API tokens', icon: 'code', need: 'api.manage', hidden: true },
      { to: '/settings/webhooks', label: 'Webhooks', icon: 'link', need: 'webhooks.view', hidden: true },
      { to: '/settings/sso', label: 'Single sign-on', icon: 'lock', need: 'sso.manage' },
      { to: '/settings/modules', label: 'Widget modules', icon: 'puzzle', need: 'modules.view', hidden: true },
      { to: '/settings/import', label: 'Import', icon: 'upload', need: 'import.run', hidden: true },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/settings/audit', label: 'Audit log', icon: 'list', need: 'audit.view' },
      { to: '/settings/backup', label: 'Backup', icon: 'download', need: 'box.backup', cap: 'boxBackup' },
      { to: '/settings/impersonate', label: 'Impersonate', icon: 'swap', need: 'platform.impersonate', operator: true },
    ],
  },
];

const SETTINGS_OPEN_KEY = 'akarya.nav.settingsOpen';

/** Lets `Page` put the drawer toggle in its own top bar on small screens. */
const NavContext = createContext<{ openNav: () => void; navOpen: boolean; alerts: number } | null>(null);

export function AppShell(): ReactNode {
  const { user, logout, can } = useAuth();
  const { branding } = useBranding();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const [openAlerts, setOpenAlerts] = useState(0);
  // Below the 900px breakpoint the sidebar is an off-canvas drawer.
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const inSettings = pathname.startsWith('/settings/');
  const [settingsOpen, setSettingsOpen] = useState(() => {
    try {
      return localStorage.getItem(SETTINGS_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => setNavOpen(false), [pathname]);

  // Arriving on a settings screen (deep link, bookmark) shows where you are.
  useEffect(() => {
    if (inSettings) setSettingsOpen(true);
  }, [inSettings]);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_OPEN_KEY, settingsOpen ? '1' : '0');
    } catch {
      // Storage blocked: the section just won't remember its state.
    }
  }, [settingsOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.classList.add('no-scroll');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('no-scroll');
    };
  }, [navOpen]);

  useEffect(() => {
    if (!can('alerts.view')) return;
    const tick = (): void => {
      void api
        .alerts()
        .then((r) => setOpenAlerts(r.openCount))
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [can]);

  // Hidden while `caps` is still loading (null) to avoid a flash of a nav
  // item that then disappears on a self-hosted install with billing off.
  const visible = (groups: NavGroup[]): NavGroup[] =>
    groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) =>
            !item.hidden &&
            (!item.simpleOnly || caps?.simple === true) &&
            showsInNav(item.to, caps) &&
            (!item.need || can(item.need)) &&
            (!item.cap || caps?.[item.cap]) &&
            (!item.operator || user?.canImpersonate === true),
        ),
      }))
      .filter((g) => g.items.length > 0);
  const mainNav = visible(NAV);
  const settingsNav = visible(SETTINGS_NAV);

  const renderItem = (item: NavItem): ReactNode => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        isActive || item.also?.some((p) => pathname.startsWith(p)) ? 'nav-link active' : 'nav-link'
      }
    >
      <Icon name={item.icon} />
      <span className="nav-label">{item.label}</span>
      {item.to === '/alerts' && openAlerts > 0 ? (
        <span className="nav-count" aria-label={`${openAlerts} open`}>
          {openAlerts > 99 ? '99+' : openAlerts}
        </span>
      ) : null}
    </NavLink>
  );

  const brand = (
    <div className="brand">
      {branding.logoUrl ? (
        <img src={branding.logoUrl} alt={branding.productName} style={{ maxHeight: 28, maxWidth: '100%' }} />
      ) : (
        branding.productName
      )}
    </div>
  );

  const nav = { openNav: () => setNavOpen(true), navOpen, alerts: openAlerts };

  return (
    <NavContext.Provider value={nav}>
      <SosProvider>
        <div className="shell">
          {/* Fallback bar for screens without a `Page` top bar (e.g. "no access"). */}
          <div className="mobile-bar">
            <NavToggle />
            {brand}
          </div>
          {navOpen ? <div className="nav-scrim" onClick={() => setNavOpen(false)} /> : null}
          <nav id="app-nav" className={navOpen ? 'sidebar open' : 'sidebar'} aria-label="Main">
            <div className="sidebar-head">
              {brand}
              <button className="btn ghost nav-close" aria-label="Close navigation" onClick={() => setNavOpen(false)}>
                <Icon name="close" />
              </button>
            </div>

            <div className="sidebar-scroll">
              {mainNav.map((group) => (
                <div className="nav-group" key={group.label}>
                  <div className="nav-section">{group.label}</div>
                  {group.items.map(renderItem)}
                </div>
              ))}

              {settingsNav.length > 0 ? (
                <div className="nav-group">
                  <button
                    type="button"
                    className="nav-section nav-section-toggle"
                    aria-expanded={settingsOpen}
                    aria-controls="nav-settings"
                    onClick={() => setSettingsOpen((v) => !v)}
                  >
                    <span>Settings</span>
                    <Icon name={settingsOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                  </button>
                  {settingsOpen ? (
                    <div id="nav-settings">
                      {settingsNav.map((group) => (
                        <div key={group.label}>
                          {settingsNav.length > 1 && !caps?.simple ? <div className="nav-subsection">{group.label}</div> : null}
                          {group.items.map(renderItem)}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="who">
              <span className="avatar" aria-hidden="true">
                {initials(user?.name ?? '')}
              </span>
              <div className="who-text">
                <div className="who-name">{user?.name}</div>
                <div className="who-meta">
                  {user?.roleLabel ?? user?.role} · {caps?.tier ?? '…'}
                  {caps ? (caps.push ? ' · push' : ' · poll-only') : ''}
                </div>
              </div>
              <button
                className="btn ghost icon-btn"
                title="Sign out"
                aria-label="Sign out"
                onClick={() => {
                  void logout().then(() => navigate('/login'));
                }}
              >
                <Icon name="logout" />
              </button>
            </div>
          </nav>
          <div className="main">
            {user?.impersonator ? <ImpersonationBanner /> : null}
            {user?.restrictedView ? (
              <div className="impersonation-banner" role="status">
                <span>Demo view: you see the layouts, media and displays you created, and the ones shared with you.</span>
              </div>
            ) : null}
            <SosBanner />
            <Outlet />
          </div>
        </div>
      </SosProvider>
    </NavContext.Provider>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** Hamburger shown only below the drawer breakpoint; carries a dot while alerts are open. */
function NavToggle(): ReactNode {
  const nav = useContext(NavContext);
  if (!nav) return null;
  return (
    <button
      className="btn ghost nav-toggle"
      aria-label={nav.alerts > 0 ? `Open navigation (${nav.alerts} open alerts)` : 'Open navigation'}
      aria-expanded={nav.navOpen}
      aria-controls="app-nav"
      onClick={nav.openNav}
    >
      <Icon name="burger" size={20} />
      {nav.alerts > 0 ? <span className="nav-toggle-dot" /> : null}
    </button>
  );
}

/** Shown on every screen while an operator is signed in as a customer's user. */
function ImpersonationBanner(): ReactNode {
  const { user, stopImpersonating } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user?.impersonator) return null;

  return (
    <div className="impersonation-banner" role="status">
      <span>
        Signed in as <strong>{user.name}</strong> ({user.email}) by {user.impersonator.name}
        {user.impersonationExpiresAt
          ? ` · ends ${new Date(user.impersonationExpiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : ''}
      </span>
      <button
        className="btn small"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void stopImpersonating().catch(() => setBusy(false));
        }}
      >
        {busy ? 'Switching back…' : 'Stop impersonating'}
      </button>
    </div>
  );
}

/** Shown on every screen while emergency SOS is on (ADR-0029), so nobody forgets to clear it. */
function SosBanner(): ReactNode {
  const { sos, setSos } = useSos();
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  if (!sos?.active) return null;

  return (
    <div className="sos-banner" role="alert">
      <span>
        <strong>SOS active on every display</strong>
        {sos.activatedBy ? ` · by ${sos.activatedBy.name}` : ''}
      </span>
      <div className="row">
        {pathname !== '/sos' ? (
          <NavLink className="btn small" to="/sos">
            Details
          </NavLink>
        ) : null}
        {can('sos.activate') ? (
          <button
            className="btn small"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void api
                .clearSos()
                .then(setSos)
                .catch(() => {})
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Clearing…' : 'Clear SOS'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

const ICONS = {
  home: 'M3 9.5 10 3l7 6.5V17H3zM8 17v-5h4v5',
  dashboard: 'M3 3h6v8H3zM11 3h6v5h-6zM11 10h6v7h-6zM3 13h6v4H3z',
  bell: 'M5 14V9a5 5 0 0 1 10 0v5l1.5 2h-13zM8.5 18a1.5 1.5 0 0 0 3 0',
  siren: 'M5.5 15v-4a4.5 4.5 0 0 1 9 0v4M3.5 15h13v2.5h-13zM10 2.5v1.5M3.6 5.1l1.1 1.1M16.4 5.1l-1.1 1.1',
  chart: 'M3 17h14M6 14V9M10 14V5M14 14v-3',
  screen: 'M2.5 4h15v9h-15zM7 17h6M10 13v4',
  groups: 'M2 7h11v7H2zM6 4h12v7',
  key: 'M8 12a4 4 0 1 1 3-3l6 6v2h-2v-1.5h-1.5V14L11 11.5M6.5 8.5h.01',
  image: 'M3 4h14v12H3zM3 13l4-4 4 4 2-2 4 4M13 7.5h.01',
  layout: 'M3 3h14v14H3zM3 8h14M8 8v9',
  menu: 'M4 3h12v14H4zM7 7h6M7 10h6M7 13h4',
  user: 'M10 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3.5 17.5a6.5 6.5 0 0 1 13 0',
  shield: 'M10 2.5l6 2.5v4.5c0 4-2.6 6.8-6 8-3.4-1.2-6-4-6-8V5zM7.5 10l2 2 3-3.5',
  folder: 'M2.5 5.5a1 1 0 0 1 1-1h4l2 2h7a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1z',
  palette:
    'M10 2.5a7.5 7.5 0 1 0 0 15c1 0 1.5-.7 1.5-1.5 0-1.2-1-1.5-1-2.5s.8-1.5 2-1.5h1.5a3.5 3.5 0 0 0 3.5-3.5c0-3.3-3.4-6-7.5-6zM6 9.5h.01M8.5 6h.01M12.5 6.5h.01',
  card: 'M2.5 5h15v10h-15zM2.5 8.5h15M5.5 12h3',
  code: 'M7 6l-4 4 4 4M13 6l4 4-4 4',
  link: 'M8.5 11.5a3 3 0 0 0 4.2 0l2.6-2.6a3 3 0 0 0-4.2-4.2L10 5.8M11.5 8.5a3 3 0 0 0-4.2 0l-2.6 2.6a3 3 0 0 0 4.2 4.2l1.1-1.1',
  lock: 'M4.5 9h11v8.5h-11zM7 9V6.5a3 3 0 0 1 6 0V9',
  puzzle: 'M4 6h3.5a2 2 0 1 1 4 0H15v3.5a2 2 0 1 1 0 4V17H4z',
  upload: 'M10 13V3.5M6 7l4-4 4 4M3.5 13v3.5h13V13',
  download: 'M10 3.5V13M6 9l4 4 4-4M3.5 13v3.5h13V13',
  list: 'M7 5h10M7 10h10M7 15h10M3.5 5h.01M3.5 10h.01M3.5 15h.01',
  swap: 'M4 7h12l-3-3M16 13H4l3 3',
  logout: 'M8 3.5H4v13h4M12 6.5 15.5 10 12 13.5M15.5 10H8',
  burger: 'M3 5h14M3 10h14M3 15h14',
  close: 'M5 5l10 10M15 5L5 15',
  chevronDown: 'M5 8l5 5 5-5',
  chevronUp: 'M5 12l5-5 5 5',
} as const;

type IconName = keyof typeof ICONS;

function Icon({ name, size = 18 }: { name: IconName; size?: number }): ReactNode {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

/** Standard page frame: a titled top bar plus a constrained content column. */
export function Page({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <header className="topbar">
        <div className="topbar-title">
          <NavToggle />
          <h1>{title}</h1>
        </div>
        {actions ? <div className="row wrap topbar-actions">{actions}</div> : null}
      </header>
      <div className="content">{children}</div>
    </>
  );
}
